import React from 'react';
import { Building2, Users, Crown, UserCheck, Plus, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const WorkspaceSelector = ({ workspaces, onSelectWorkspace, onCreateWorkspace, onAddWorkspace, loading }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/salesqualifyd-logo.png" 
            alt="SALESQUALIFYD" 
            className="h-12 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Choose Your Workspace
          </h1>
          <p className="text-gray-600">
            Select a workspace to continue
          </p>
        </div>

        {/* Workspace Tiles */}
        <div className="grid gap-4">
          {workspaces.map((workspace) => (
            <button
              key={workspace.id}
              onClick={() => onSelectWorkspace(workspace)}
              disabled={loading}
              className="w-full bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-red-600 hover:shadow-lg transition-all duration-200 text-left group disabled:opacity-50"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div className={`
                    w-14 h-14 rounded-xl flex items-center justify-center
                    ${workspace.workspace_type === 'creator' 
                      ? 'bg-gradient-to-br from-red-500 to-red-500' 
                      : 'bg-gradient-to-br from-blue-500 to-purple-500'}
                  `}>
                    {workspace.workspace_type === 'creator' ? (
                      <Crown className="w-7 h-7 text-white" />
                    ) : (
                      <UserCheck className="w-7 h-7 text-white" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {workspace.name}
                      </h3>
                      <Badge 
                        variant={workspace.workspace_type === 'creator' ? 'default' : 'secondary'}
                        className={workspace.workspace_type === 'creator' 
                          ? 'bg-red-100 text-red-900 hover:bg-red-100' 
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-100'}
                      >
                        {workspace.workspace_type === 'creator' ? 'Creator' : 'Collaborator'}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-2">
                      {workspace.workspace_type === 'creator' 
                        ? 'You own this workspace' 
                        : `Owned by ${workspace.owner_name}`}
                    </p>

                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {workspace.member_count} {workspace.member_count === 1 ? 'member' : 'members'}
                      </span>
                      <span className="flex items-center">
                        <Building2 className="w-4 h-4 mr-1" />
                        Role: {workspace.role}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-red-700 group-hover:translate-x-1 transition-all" />
              </div>
            </button>
          ))}
        </div>

        {/* Create Workspace Button - Only show if user doesn&apos;t have a creator workspace (Collaborator only) */}
        {!workspaces.some(w => w.workspace_type === 'creator') && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gradient-to-br from-gray-50 to-gray-100 text-gray-500">
                  or
                </span>
              </div>
            </div>

            <Button
              onClick={onCreateWorkspace}
              variant="outline"
              className="w-full mt-6 py-6 border-2 border-dashed border-gray-300 hover:border-red-600 hover:bg-red-50"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your Own Workspace
            </Button>
            <p className="text-center text-sm text-gray-500 mt-2">
              Start your own workspace with a paid subscription
            </p>
          </div>
        )}

        {/* Subscribe Additional Workspace - Only show for Creators */}
        {workspaces.some(w => w.workspace_type === 'creator') && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Need another workspace? Select one above, then use{' '}
              <span className="text-red-800 font-medium">&quot;Subscribe Additional Workspace&quot;</span>{' '}
              from the sidebar menu.
            </p>
          </div>
        )}

        {/* Org ID Info */}
        <p className="text-center text-xs text-gray-400 mt-8">
          Your workspaces are securely isolated. Data from one workspace is not accessible in another.
        </p>
      </div>
    </div>
  );
};

export default WorkspaceSelector;
